import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import AddNewButton from './AddNewButton';
import AddNewModals from './AddNewModals';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { faEyes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Program } from '../Reports/NewReportDialog';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { NPOUser } from './NewProgram/api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

interface RenderEmptyReportStateProps {
  searchText: string;
  defaultProgramName: string | undefined;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reloadProgramList: () => unknown;
}

const StyledDiv = styled.div`
  padding: 60px ${SPACERS.large};
  text-align: center;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  > * {
    margin: auto;
  }
  .icon,
  button {
    margin: ${SPACERS.small} auto 0;
  }
  .icon {
    font-size: 52px;
    color: ${COLORS.ctaPrimary};
    margin-bottom: ${SPACERS.medium};
  }
  > * {
    margin-bottom: ${SPACERS.tiny};
  }
`;

export const RenderEmptyReportState: React.FC<RenderEmptyReportStateProps> = ({
  searchText,
  defaultProgramName,
  allNPOUsers,
  programsList,
  reloadProgramList,
}) => {
  const [showLoading, setShowLoading] = useState<boolean>(true);
  const [openNewReport, setOpenNewReport] = useState<boolean>(false);
  const [openNewProgram, setOpenNewProgram] = useState<boolean>(false);
  const onNewReportClick = useCallback(() => setOpenNewReport(true), [
    setOpenNewReport,
  ]);
  const onNewProgramClick = useCallback(() => setOpenNewProgram(true), [
    setOpenNewProgram,
  ]);

  const addButtons = useMemo(
    () => [
      {
        text: 'New Report',
        onClick: onNewReportClick,
      },
      {
        text: 'New Program',
        onClick: onNewProgramClick,
      },
    ],
    [onNewReportClick, onNewProgramClick],
  );

  useEffect(() => {
    window.setTimeout(() => setShowLoading(false), 1000);
  }, [setShowLoading]);

  if (showLoading) {
    return <TrueImpactLoading />;
  }

  return (
    <StyledDiv>
      <FontAwesomeIcon icon={faEyes} className="icon" />
      <TrueImpactText variant="body" align="center">
        It looks like you don’t have any reports for{' '}
        {searchText ? <strong>{`"${searchText}"`}</strong> : 'your filters'}.
      </TrueImpactText>
      <TrueImpactText variant="body" align="center">
        If you’re looking for another year or program, you can change the filter
        above, or add a new report and program now.
      </TrueImpactText>
      <AddNewButton onAdd={addButtons} />
      <AddNewModals
        allNPOUsers={allNPOUsers}
        programsList={programsList}
        openNewProgram={openNewProgram}
        openNewReport={openNewReport}
        reloadProgramList={reloadProgramList}
        setOpenNewProgram={setOpenNewProgram}
        setOpenNewReport={setOpenNewReport}
        handleReportCountLoad={undefined}
        defaultProgramName={defaultProgramName}
      />
    </StyledDiv>
  );
};
